import { useState, useCallback } from "react";
import { useSidebar } from "../context/SidebarContext";
import axios from "../lib/axios";
import { Rocket } from "lucide-react";

/**
 * Scalekit (d2c dashboard) opener for the merchant sidebar.
 *
 * Follows the existing iSight external-tool pattern: on click we ask the
 * Neo backend for a short-lived SSO token (`POST /api/scalekit/sso-token`),
 * then open the d2c "Scalekit" frontend at its `/sso` route in a new tab.
 * d2c exchanges the token for its own session — no second login screen.
 */
const SCALEKIT_BASE_URL =
  import.meta.env.VITE_SCALEKIT_URL || "http://localhost:5173";

// Scalekit wordmark assets (public/), light/dark aware like the iSight button.
// Black logo on light sidebar, white logo on dark sidebar.
const SCALEKIT_LOGO_LIGHT = "/Scalekit-black%201.svg";
const SCALEKIT_LOGO_DARK = "/Scalekit-white%201.svg";

interface ScalekitOpenButtonProps {
  variant?: "sidebar" | "header";
  /** When true the button is visible but non-interactive (e.g. billing). */
  navigationDisabled?: boolean;
  /** When provided, render this text label instead of the brand wordmark. */
  label?: string;
}

export function ScalekitOpenButton({
  variant = "sidebar",
  navigationDisabled = false,
  label,
}: ScalekitOpenButtonProps) {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expanded = isExpanded || isHovered || isMobileOpen;

  const handleOpenScalekit = useCallback(async () => {
    if (navigationDisabled) return;

    // Open the tab synchronously within the click gesture so popup blockers
    // don't kill it; we navigate it to the SSO URL once the token arrives.
    const popup = window.open("about:blank", "_blank");

    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post("/scalekit/sso-token");
      const token = data?.token;
      if (!token) {
        throw new Error("No SSO token returned.");
      }
      const url = `${SCALEKIT_BASE_URL}/sso?token=${encodeURIComponent(token)}`;
      if (popup && !popup.closed) {
        popup.location.href = url;
      } else {
        // Fallback if the tab was blocked/closed.
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err: any) {
      console.error("Scalekit SSO error:", err);
      popup?.close?.();
      setError(
        err?.response?.data?.message ||
          "Unable to open Scalekit right now. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [navigationDisabled]);

  return (
    <div className={variant === "sidebar" ? "shrink-0" : ""}>
      <button
        type="button"
        onClick={handleOpenScalekit}
        disabled={navigationDisabled || loading}
        title={
          !expanded
            ? "Scalekit"
            : error
              ? error
              : "Open Scalekit"
        }
        className={`menu-item group w-full flex items-center ${
          loading
            ? "menu-item-active"
            : "menu-item-inactive"
        } ${
          !expanded ? "lg:justify-start" : "justify-start px-3"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {expanded ? (
          <>
            {label ? (
              <>
                <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
                  <Rocket className="h-5 w-5 text-[#A3A3A3] group-hover:text-[var(--accent)]" />
                </span>
                <span className="flex min-w-0 flex-1 items-baseline gap-[3px]">
                  <span className="min-w-0 truncate text-left">{label}</span>
                  <span className="tag-sup tag-sup--free">Free</span>
                </span>
              </>
            ) : (
              <span className="flex min-w-0  items-center">
                {loading ? (
                  <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
                ) : (
                  <>
                    <img
                      src={SCALEKIT_LOGO_LIGHT}
                      alt="Scalekit"
                      className="object-contain dark:hidden"
                      style={{ height: 22, width: "auto", maxWidth: "100%" }}
                    />
                    <img
                      src={SCALEKIT_LOGO_DARK}
                      alt="Scalekit"
                      className="hidden object-contain dark:block"
                      style={{ height: 22, width: "auto", maxWidth: "100%" }}
                    />
                  </>
                )}
              </span>
            )}
            {!label && (
              <span className="flex shrink-0 items-center">
                {loading ? (
                  <span className="whitespace-nowrap text-xs font-semibold text-[var(--accent)]">
                    Opening…
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full border border-transparent bg-[var(--okbg)] px-2 py-[3px] text-[10px] font-semibold uppercase tracking-wide text-[var(--okt)]">
                    Free
                  </span>
                )}
              </span>
            )}
          </>
        ) : (
          <span className="menu-item-icon-size transition-transform duration-200 group-hover:scale-110">
            {loading ? (
              <span className="block h-5 w-5 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
            ) : (
              <Rocket className="h-5 w-5 text-[#A3A3A3] group-hover:text-[var(--accent)]" />
            )}
          </span>
        )}
      </button>
      {expanded && error && variant === "sidebar" && (
        <p className="px-3 pt-1 text-[11px] leading-snug text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

export default ScalekitOpenButton;
